<script setup>
defineProps({
    allUnpaidInvoices: {
        type: Array,
        required: true,
    },
    toggleInvoice: {
        type: Function,
        required: true,
    },
    isSelected: {
        type: Function,
        required: true,
    },
    formatDate: {
        type: Function,
        required: true,
    },
    moment: {
        type: Function,
        required: true,
    },
})
</script>

<template>
    <template v-for="inv in allUnpaidInvoices" :key="inv.id">
        <tr class="hidden md:table-row hover:bg-theme-bg-light transition-colors duration-200 cursor-pointer"
            @click="toggleInvoice(inv)">
            <td class="whitespace-nowrap py-4 pl-5 pr-3 text-sm sm:pl-6">
                <div class="checkbox">
                    <input type="checkbox" :checked="isSelected(inv)" @click.stop="toggleInvoice(inv)" />
                </div>
            </td>
            <td class="whitespace-nowrap px-3 py-4 text-sm font-medium text-theme-secondary">
                <a :href="'/invoice/' + inv.id" target="_blank"
                    class="hover:text-brand-primary flex items-center gap-1 uppercase" @click.stop>
                    {{ inv.invoice_number }}
                    <Icon name="link-external" class="w-3 h-3 opacity-50" />
                </a>
            </td>
            <td class="whitespace-nowrap px-3 py-4 text-sm text-theme-body uppercase">
                {{ inv.order_number }}
            </td>
            <td class="whitespace-nowrap px-3 py-4 text-sm text-theme-body">
                {{ formatDate(inv.created_at) }}
            </td>
            <td class="whitespace-nowrap px-3 py-4 text-sm text-theme-body">
                {{ formatDate(inv.due_at) }}
            </td>
            <td class="whitespace-nowrap px-3 py-4 text-sm font-semibold text-theme-secondary">
                ${{ inv.payable_amount }}
            </td>
            <td class="whitespace-nowrap px-0 py-4 text-sm">
                <span v-if="
                    inv.paid ||
                    inv.failed ||
                    moment(inv.due_at).isBefore(moment(), 'day')
                " class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium" :class="inv.paid
                    ? 'bg-green-100 text-green-800'
                    : inv.failed
                        ? 'bg-red-100 text-red-800'
                        : 'bg-orange-100 text-orange-800'
                    ">
                    {{
                        inv.paid ? "Paid" : inv.failed ? "Failed" : "Overdue"
                    }}
                </span>
            </td>
        </tr>

        <tr class="md:hidden">
            <td colspan="7" class="px-4 py-3">
                <div class="space-y-2 cursor-pointer" @click="toggleInvoice(inv)">
                    <div class="flex items-start justify-between gap-2">
                        <div class="checkbox pt-1">
                            <input type="checkbox" :checked="isSelected(inv)" @click.stop="toggleInvoice(inv)" />
                        </div>
                        <div class="flex-1 min-w-0">
                            <div class="flex items-center gap-2 min-w-0">
                                <a :href="'/invoice/' + inv.id" target="_blank"
                                    class="hover:text-brand-primary inline-flex items-center gap-1 uppercase text-sm font-semibold text-theme-secondary truncate"
                                    @click.stop>
                                    {{ inv.invoice_number }}
                                    <Icon name="link-external" class="w-3 h-3 opacity-50 flex-shrink-0" />
                                </a>
                                <span v-if="
                                    inv.paid ||
                                    inv.failed ||
                                    moment(inv.due_at).isBefore(moment(), 'day')
                                " class="inline-flex items-center rounded-full px-2 py-0.5 text-tiny font-medium leading-none flex-shrink-0"
                                    :class="inv.paid
                                        ? 'bg-green-100 text-green-800'
                                        : inv.failed
                                            ? 'bg-red-100 text-red-800'
                                            : ' bg-yellow-100 text-yellow-800'
                                        ">
                                    {{
                                        inv.paid ? "Paid" : inv.failed ? "Failed" : "Overdue"
                                    }}
                                </span>
                            </div>
                            <p class="text-xs text-theme-body mt-1">
                                #{{ inv.order_number }}
                                <span class="mx-1 text-tiny">&bull;</span>
                                Due {{ formatDate(inv.due_at) }}
                            </p>
                        </div>
                        <p class="text-sm font-semibold text-theme-secondary leading-none pt-0.5">
                            ${{ inv.payable_amount }}
                        </p>
                    </div>
                </div>
            </td>
        </tr>
    </template>
</template>

<style lang="scss" scoped>
.checkbox {
    @apply flex items-center space-x-3 select-none cursor-pointer;

    &.align-start {
        @apply items-start;
    }

    input {
        @apply h-4 w-4 border-2 border-brand-secondary border-opacity-50 rounded text-brand-secondary focus:ring-transparent cursor-pointer;

        &:focus-visible {
            @apply border-brand-secondary ring-2 ring-brand-gray;
        }
    }

    span {
        @apply font-medium text-base text-theme-body leading-none;

        &.xs {
            @apply text-sm font-normal;
        }
    }
}
</style>